import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { CalendarDays, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { CreneauSelect } from './IntervenantsJour';

const JOURS = [['1', 'Lun'], ['2', 'Mar'], ['3', 'Mer'], ['4', 'Jeu'], ['5', 'Ven'], ['6', 'Sam']];
const dow = (d) => String(new Date(d + 'T00:00:00').getDay());

// Exceptions par jour de la semaine : un formateur différent (ou un intervenant supplémentaire) sur tous les lundis, mardis…
export const ExceptionsJoursSemaine = ({ planning, formateurs, creneaux, referentId, onPlanningChange }) => {
  const [jours, setJours] = useState([]);
  const [formateurId, setFormateurId] = useState('none');
  const [creneau, setCreneau] = useState('journee');
  const [mode, setMode] = useState('principal'); // principal | intervenant
  const presents = new Set(planning.filter(p => p.type_journee !== 'stage').map(p => dow(p.date)));
  const cibles = planning.filter(p => p.type_journee !== 'stage' && jours.includes(dow(p.date)));
  const toggle = (j) => setJours(s => s.includes(j) ? s.filter(x => x !== j) : [...s, j]);
  const nom = () => { const f = formateurs.find(x => x.id === formateurId); return f ? `${f.nom} ${f.prenom}` : ''; };
  // Tous les collaborateurs actifs (pas seulement les formateurs sous contrat) : le référent en tête
  const actifs = formateurs.filter(f => f.is_active !== false);
  const referent = actifs.find(f => f.id === referentId);
  const autres = actifs.filter(f => f.id !== referentId);

  const appliquer = () => {
    if (formateurId === 'none' || cibles.length === 0) return;
    const ids = new Set(cibles.map(p => p.date));
    if (mode === 'principal') {
      onPlanningChange(planning.map(p => ids.has(p.date) ? { ...p, formateur_id: formateurId, formateur_creneau: creneau === 'journee' ? undefined : creneau } : p));
      toast.success(`${nom()} affecté(e) comme formateur principal sur ${cibles.length} journée(s) (${jours.map(j => JOURS.find(x => x[0] === j)?.[1]).join(', ')})`);
    } else {
      onPlanningChange(planning.map(p => {
        if (!ids.has(p.date) || p.formateur_id === formateurId || (p.intervenants || []).some(i => i.formateur_id === formateurId)) return p;
        return { ...p, intervenants: [...(p.intervenants || []), { formateur_id: formateurId, creneau, module_ids: [] }] };
      }));
      toast.success(`${nom()} ajouté(e) comme intervenant sur ${cibles.length} journée(s)`);
    }
    setJours([]);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2" data-testid="exceptions-jours-semaine">
      <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide mr-1 inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Exceptions par jour</span>
      <div className="flex items-center gap-1">
        {JOURS.map(([j, lib]) => (
          <button key={j} type="button" disabled={!presents.has(j)} onClick={() => toggle(j)} title={presents.has(j) ? `${planning.filter(p => p.type_journee !== 'stage' && dow(p.date) === j).length} journée(s)` : 'Aucune journée ce jour-là'}
            className={`h-7 w-9 rounded text-[11px] font-semibold border transition-colors ${jours.includes(j) ? 'bg-amber-500 text-white border-amber-600' : presents.has(j) ? 'bg-white text-slate-700 border-slate-300 hover:border-amber-400' : 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'}`} data-testid={`exception-jour-${j}`}>{lib}</button>
        ))}
      </div>
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger className="h-7 text-xs w-[150px] bg-white" data-testid="exception-mode"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="principal">Formateur principal</SelectItem>
          <SelectItem value="intervenant">+ Intervenant</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex items-center gap-1.5">
        {mode === 'principal' ? <Users className="w-3.5 h-3.5 text-amber-600" /> : <UserPlus className="w-3.5 h-3.5 text-amber-600" />}
        <Select value={formateurId} onValueChange={setFormateurId}>
          <SelectTrigger className="h-7 text-xs w-[220px] bg-white" data-testid="exception-formateur-select"><SelectValue placeholder="Collaborateur" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none"><span className="text-slate-400">Aucun</span></SelectItem>
            {referent && <SelectItem value={referent.id} className="font-semibold text-indigo-700">{referent.nom} {referent.prenom} — Référent</SelectItem>}
            {autres.map(f => <SelectItem key={f.id} value={f.id} data-testid={`exception-collab-${f.id}`}>{f.nom} {f.prenom}{f.statut_intervenant === 'externe' ? ' (ext.)' : ''}{f.sous_contrat === false && f.statut_intervenant !== 'externe' ? ' · hors contrat actif' : ''}{!(f.fonctions || ['formateur']).includes('formateur') ? ` · ${(f.fonctions || []).join(', ')}` : ''}</SelectItem>)}
          </SelectContent>
        </Select>
        <CreneauSelect value={creneau} onChange={setCreneau} creneaux={creneaux} testId="exception-creneau" className="w-[100px] bg-white" />
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-amber-300" onClick={appliquer} disabled={formateurId === 'none' || cibles.length === 0} data-testid="exception-apply">
          Appliquer{cibles.length ? ` (${cibles.length} j.)` : ''}
        </Button>
      </div>
    </div>
  );
};
